// ============================================================
// HoverChat Admin — System Prompts Page
// Top-level page for editing the system prompt, context template,
// and no-context fallback message.
// ============================================================

import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import {
  Save, AlertCircle, CheckCircle2, RefreshCw, Info
} from 'lucide-react';

export function SystemPromptsPage() {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenants, setTenants] = useState<any[]>([]);
  const [prompt, setPrompt] = useState<any>(null);
  const [original, setOriginal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Load tenants, pick the oldest one (primary tenant)
  useEffect(() => {
    api.listTenants()
      .then(({ tenants }) => {
        // Sort oldest first so the primary tenant is default
        const sorted = [...tenants].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        setTenants(sorted);
        if (sorted.length > 0) {
          setTenantId(sorted[0].id);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Load prompts when tenant changes
  useEffect(() => {
    if (tenantId) loadPrompts();
  }, [tenantId]);

  // Auto-dismiss messages
  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const loadPrompts = async () => {
    if (!tenantId) return;
    try {
      const { prompt: data } = await api.getPrompts(tenantId);
      setPrompt(data);
      setOriginal(JSON.parse(JSON.stringify(data)));
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    }
  };

  const hasChanges = prompt && original && JSON.stringify(prompt) !== JSON.stringify(original);

  const handleSave = async () => {
    if (!tenantId || !prompt) return;
    setSaving(true);
    setMessage(null);
    try {
      await api.updatePrompts(tenantId, prompt);
      setOriginal(JSON.parse(JSON.stringify(prompt)));
      setMessage({ text: 'System prompts saved successfully!', type: 'success' });
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    }
    setSaving(false);
  };

  const handleReset = () => {
    if (original) {
      setPrompt(JSON.parse(JSON.stringify(original)));
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-4 bg-gray-200 rounded w-96"></div>
        </div>
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-10 w-10 text-yellow-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-yellow-800">No Tenants Found</h2>
          <p className="text-sm text-yellow-700 mt-1">Create a tenant first before configuring prompts.</p>
        </div>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Prompts</h1>
          <p className="text-gray-500 mt-1">
            Configure how the AI chatbot behaves. These instructions are sent with every message.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasChanges && (
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Discard
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-primary/90 transition-colors"
          >
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Tenant selector (if multiple) */}
      {tenants.length > 1 && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Tenant</label>
          <select
            value={tenantId}
            onChange={e => setTenantId(e.target.value)}
            className="w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
          >
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Status message */}
      {message && (
        <div className={`mb-6 flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${
          message.type === 'error'
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {message.type === 'error' ? <AlertCircle className="h-4 w-4 flex-shrink-0" /> : <CheckCircle2 className="h-4 w-4 flex-shrink-0" />}
          {message.text}
        </div>
      )}

      {/* Unsaved changes banner */}
      {hasChanges && (
        <div className="mb-6 flex items-center gap-2 px-4 py-3 rounded-lg text-sm bg-amber-50 text-amber-700 border border-amber-200">
          <Info className="h-4 w-4 flex-shrink-0" />
          You have unsaved changes.
        </div>
      )}

      <div className="space-y-6">
        {/* System Prompt */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">System Prompt</h2>
            <p className="text-sm text-gray-500 mt-1">
              This is the main instruction that tells the AI how to behave. It is included at the start of every conversation.
              Write in second person ("You are a helpful assistant for...").
            </p>
          </div>
          <textarea
            value={prompt.system_prompt || ''}
            onChange={e => setPrompt({ ...prompt, system_prompt: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm font-mono leading-relaxed focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
            rows={14}
            placeholder="You are a helpful assistant for [Company Name]. You help customers with questions about products, pricing, and support.&#10;&#10;Key guidelines:&#10;- Be friendly and professional&#10;- Answer using the provided knowledge base context when available&#10;- If you don't know, say so honestly&#10;- Keep responses concise"
          />
          <p className="text-xs text-gray-400 mt-2">{(prompt.system_prompt || '').length.toLocaleString()} characters</p>
        </div>

        {/* Context Injection Template */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Context Injection Template</h2>
            <p className="text-sm text-gray-500 mt-1">
              When the chatbot finds relevant knowledge base results, this template is appended to the system prompt.
              Use <code className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded text-xs font-mono">{'{context}'}</code> as the placeholder where the search results will be inserted.
            </p>
          </div>
          <textarea
            value={prompt.context_injection_template || ''}
            onChange={e => setPrompt({ ...prompt, context_injection_template: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm font-mono leading-relaxed focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
            rows={6}
            placeholder="Use the following information to help answer the user's question:&#10;&#10;{context}&#10;&#10;Base your answer on this information. Cite sources when possible."
          />
          <p className="text-xs text-gray-400 mt-2">{(prompt.context_injection_template || '').length.toLocaleString()} characters</p>
        </div>

        {/* No-Context Message */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">No-Context Fallback</h2>
            <p className="text-sm text-gray-500 mt-1">
              This message is appended to the system prompt when no knowledge base results are found for the user's question.
              It helps the AI handle questions outside the knowledge base gracefully.
            </p>
          </div>
          <textarea
            value={prompt.no_context_message || ''}
            onChange={e => setPrompt({ ...prompt, no_context_message: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm font-mono leading-relaxed focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
            rows={4}
            placeholder="No specific information was found in the knowledge base for this question. Answer based on your general knowledge, but let the user know you may not have the most specific details."
          />
          <p className="text-xs text-gray-400 mt-2">{(prompt.no_context_message || '').length.toLocaleString()} characters</p>
        </div>

        {/* Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h3 className="text-sm font-medium text-blue-900 mb-2">Tips for effective prompts</h3>
          <ul className="text-sm text-blue-800 space-y-1.5 list-disc list-inside">
            <li>Be specific about the company, products, and tone of voice</li>
            <li>Include instructions about how to handle unknown questions</li>
            <li>Mention any topics the chatbot should avoid</li>
            <li>The context injection template should tell the AI to prioritize KB results over general knowledge</li>
            <li>Changes take effect immediately for new conversations</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
